import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  RefreshCw,
  Save,
  Shield,
  ShieldCheck,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';

type ObservableType = 'ipv4-addr' | 'ipv6-addr' | 'domain-name' | 'url' | 'file-md5' | 'file-sha1' | 'file-sha256' | 'email-addr';
type PatternMode = 'builder' | 'freeText';
type TargetProduct = 'Microsoft Sentinel' | 'Microsoft Defender for Endpoint';

interface TargetAuthValidation {
  ok?: boolean;
  destination?: string;
  authMode?: string;
  targetTenantId?: string;
  tokenTenantId?: string;
  tenantMatch?: boolean;
  workspaceId?: string;
  message?: string;
}

interface ValidationCustomer {
  id: string;
  name: string;
  tenantId?: string;
  subscription?: string;
  resourceGroup?: string;
  workspaceId?: string;
  workspaceName?: string;
  lighthouseMode?: boolean;
  authMode?: string;
}

interface ValidationResponse {
  valid: boolean;
  errors: string[];
  warnings: string[];
  pattern?: string;
  detectedType?: string;
  customerId?: string | null;
  customer?: ValidationCustomer | null;
  targetAuth?: TargetAuthValidation | null;
}

interface UploadResponse {
  ok?: boolean;
  id?: string;
  stixId?: string;
  mdeIndicatorId?: string | null;
  validation?: ValidationResponse;
  response?: unknown;
  error?: string;
  message?: string;
  customerId?: string;
  targetTenantId?: string;
  tokenTenantId?: string;
  authMode?: string;
  tenantMatch?: boolean;
}

const observableOptions: { id: ObservableType; label: string; placeholder: string; mde: boolean }[] = [
  { id: 'ipv4-addr', label: 'IPv4 address', placeholder: '1.2.3.4', mde: true },
  { id: 'ipv6-addr', label: 'IPv6 address', placeholder: '2001:db8::1', mde: true },
  { id: 'domain-name', label: 'Domain', placeholder: 'evil.example.com', mde: true },
  { id: 'url', label: 'URL', placeholder: 'https://evil.example.com/login', mde: true },
  { id: 'file-md5', label: 'File hash - MD5', placeholder: '32 hex characters', mde: true },
  { id: 'file-sha1', label: 'File hash - SHA1', placeholder: '40 hex characters', mde: true },
  { id: 'file-sha256', label: 'File hash - SHA256', placeholder: '64 hex characters', mde: true },
  { id: 'email-addr', label: 'Email address', placeholder: 'attacker@example.com', mde: false },
];

function pad(n: number): string { return String(n).padStart(2, '0'); }
function datetimeLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function addDays(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
}
function stixPath(type: ObservableType): string {
  switch (type) {
    case 'file-md5': return 'file:hashes.MD5';
    case 'file-sha1': return 'file:hashes.SHA-1';
    case 'file-sha256': return 'file:hashes.SHA-256';
    default: return `${type}:value`;
  }
}
function buildPattern(type: ObservableType, value: string): string {
  const escaped = value.trim().replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `[${stixPath(type)} = '${escaped}']`;
}
function autoName(type: ObservableType, value: string): string {
  const label = observableOptions.find((o) => o.id === type)?.label ?? type;
  const v = value.trim();
  return `Indicator - ${label} - ${v.length > 48 ? `${v.slice(0, 48)}…` : v}`;
}
function toIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
function splitList(value: string): string[] {
  return value.split(',').map((x) => x.trim()).filter(Boolean);
}
function responseText(value: unknown): string {
  if (!value) return '';
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

export function SentinelTiObjectBuilder() {
  const { customers, activeCustomerId, activeCustomer } = useCustomers();
  const { user, canCapability } = useAuth();
  const canValidate = canCapability('threat_intel', 'validate_ioc');
  const canDraft = canCapability('threat_intel', 'create_sentinel_ioc_draft');
  const canSentinelUpload = canCapability('threat_intel', 'create_sentinel_ioc');
  const canMdeUpload = canCapability('threat_intel', 'create_mde_ioc');

  // Never inherit the global/default customer for a write operation.
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);
  const customerReady = Boolean(selectedCustomerId && selectedCustomer);

  const [target, setTarget] = useState<TargetProduct>('Microsoft Sentinel');
  const [mode, setMode] = useState<PatternMode>('builder');
  const [observableType, setObservableType] = useState<ObservableType>('ipv4-addr');
  const [observableValue, setObservableValue] = useState('');
  const [freePattern, setFreePattern] = useState('');
  const [name, setName] = useState('');
  const [indicatorTypes, setIndicatorTypes] = useState('malicious-activity');
  const [killChain, setKillChain] = useState('');
  const [validFrom, setValidFrom] = useState(() => datetimeLocal(new Date()));
  const [validUntil, setValidUntil] = useState(() => datetimeLocal(addDays(30)));
  const [source, setSource] = useState('KloudSecure');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [revoked, setRevoked] = useState(false);
  const [confidenceNull, setConfidenceNull] = useState(false);
  const [confidence, setConfidence] = useState(75);
  const [tlp, setTlp] = useState('white');

  const [mdeAction, setMdeAction] = useState('Block');
  const [mdeSeverity, setMdeSeverity] = useState('Medium');
  const [mdeGenerateAlert, setMdeGenerateAlert] = useState(true);
  const [mdeRecommendedActions, setMdeRecommendedActions] = useState('Review related alerts and scope before closing.');
  const [mdeApplication, setMdeApplication] = useState('');
  const [mdeRbacGroupNames, setMdeRbacGroupNames] = useState('');
  const [mdeRbacGroupIds, setMdeRbacGroupIds] = useState('');

  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [validatedFingerprint, setValidatedFingerprint] = useState('');
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<unknown>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const generatedPattern = useMemo(() => {
    if (mode === 'freeText') return freePattern.trim();
    if (!observableValue.trim()) return '';
    return buildPattern(observableType, observableValue);
  }, [freePattern, mode, observableType, observableValue]);

  const effectiveName = name.trim() || (observableValue.trim() ? autoName(observableType, observableValue) : '');
  const selectedObservable = observableOptions.find((o) => o.id === observableType);
  const mdeSupported = Boolean(selectedObservable?.mde) && mode === 'builder';

  const payload = useMemo(() => ({
    customerId: selectedCustomerId,
    objectType: 'indicator',
    patternMode: mode,
    observableType,
    observableValue: observableValue.trim(),
    pattern: generatedPattern,
    name: effectiveName,
    indicatorTypes: splitList(indicatorTypes),
    killChainPhases: splitList(killChain),
    validFrom: toIso(validFrom),
    validUntil: toIso(validUntil),
    source,
    description,
    tags: splitList(tags),
    revoked,
    confidence: confidenceNull ? null : confidence,
    tlp,
    createdBy: user ? { id: user.id, email: user.email, name: user.name } : undefined,
    targetProduct: target,
    draftId: draftId ?? undefined,
    mdeAction,
    mdeSeverity,
    mdeGenerateAlert,
    mdeRecommendedActions,
    mdeApplication,
    mdeRbacGroupNames: splitList(mdeRbacGroupNames),
    mdeRbacGroupIds: splitList(mdeRbacGroupIds),
  }), [selectedCustomerId, confidence, confidenceNull, description, draftId, effectiveName, generatedPattern, indicatorTypes, killChain, mdeAction, mdeApplication, mdeGenerateAlert, mdeRbacGroupIds, mdeRbacGroupNames, mdeRecommendedActions, mdeSeverity, mode, observableType, observableValue, revoked, source, tags, target, tlp, user, validFrom, validUntil]);

  const currentFingerprint = useMemo(() => JSON.stringify({ ...payload, draftId: undefined }), [payload]);
  const validationCurrent = Boolean(validation?.valid && validatedFingerprint === currentFingerprint);
  const uploadAllowed = target === 'Microsoft Sentinel' ? canSentinelUpload : canMdeUpload;
  const submitReady = Boolean(customerReady && uploadAllowed && validationCurrent && (target === 'Microsoft Sentinel' || mdeSupported));

  useEffect(() => {
    setValidation(null);
    setValidatedFingerprint('');
    setMessage(null);
    setLastResponse(null);
    setDraftId(null);
    setConfirmOpen(false);
  }, [selectedCustomerId, target]);

  async function validate() {
    if (!canValidate || !customerReady) return;
    setValidating(true);
    setMessage(null);
    setLastResponse(null);
    setConfirmOpen(false);
    try {
      const result = await apiFetch<ValidationResponse>('/threat-intel/ioc-drafts/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setValidation(result);
      setValidatedFingerprint(result.valid ? currentFingerprint : '');
      setMessage(result.valid ? 'Validation passed. Review the target context below before submission.' : 'Validation found issues that must be fixed before submission.');
    } catch (e) {
      setValidation({ valid: false, errors: [e instanceof Error ? e.message : 'Validation failed.'], warnings: [] });
      setValidatedFingerprint('');
    } finally {
      setValidating(false);
    }
  }

  async function saveDraft() {
    if (!canDraft || !customerReady) return;
    setSaving(true);
    setMessage(null);
    setLastResponse(null);
    try {
      const result = await apiFetch<{ id: string; validation: ValidationResponse }>('/threat-intel/ioc-drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setDraftId(result.id);
      setValidation(result.validation);
      // Saving a draft only performs local syntax validation. It must never
      // make the write button submission-ready because MDE tenant-auth
      // verification is performed by the explicit Validate action.
      setValidatedFingerprint('');
      setMessage(`Draft saved locally. Draft ID: ${result.id}. Validate again before submission.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not save draft.');
    } finally {
      setSaving(false);
    }
  }

  async function uploadSentinel() {
    if (!canSentinelUpload || !submitReady) return;
    setUploading(true);
    setMessage(null);
    setLastResponse(null);
    try {
      const result = await apiFetch<UploadResponse>('/threat-intel/ioc/sentinel/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, targetProduct: 'Microsoft Sentinel' }),
      });
      if (result.validation) setValidation(result.validation);
      setLastResponse(result);
      setConfirmOpen(false);
      setMessage(`Sentinel upload succeeded${result.stixId ? ` with STIX ID ${result.stixId}` : ''}.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Sentinel upload failed.');
    } finally {
      setUploading(false);
    }
  }

  async function uploadMde() {
    if (!canMdeUpload || !submitReady || !mdeSupported) return;
    setUploading(true);
    setMessage(null);
    setLastResponse(null);
    try {
      const result = await apiFetch<UploadResponse>('/threat-intel/ioc/mde/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, targetProduct: 'Microsoft Defender for Endpoint' }),
      });
      if (result.validation) setValidation(result.validation);
      setLastResponse(result);
      setConfirmOpen(false);
      const tenantNote = result.targetTenantId ? ` in tenant ${result.targetTenantId}` : '';
      setMessage(`MDE indicator submission succeeded${result.mdeIndicatorId ? ` with ID ${result.mdeIndicatorId}` : ''}${tenantNote}.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'MDE indicator submission failed.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/10 via-card to-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">Manual IoC Entry</div>
            <h3 className="mt-1 text-xl font-black text-foreground">Customer-scoped indicator creation</h3>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">The manual workflow now follows the same guarded pattern as Bulk IoC Entry: explicit customer, explicit destination, validation, target review and then submission.</p>
          </div>
          <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 text-xs text-cyan-100">
            <div className="font-black">Safety mode</div>
            <div className="mt-1">No default-tenant fallback · MDE token tenant must match the selected customer tenant.</div>
          </div>
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_390px]">
        <div className="space-y-5">
          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h4 className="font-black text-foreground">1. Customer and destination</h4>
            <p className="mt-1 text-xs text-muted-foreground">Select the customer explicitly. The global dashboard customer is displayed for reference only and is never inherited for this write.</p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Customer <span className="text-rose-400">*</span></label>
                <select value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
                  <option value="">Select customer explicitly…</option>
                  {customers.filter((customer) => customer.enabled !== false).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">Global dashboard customer: {activeCustomer?.name ?? activeCustomerId ?? 'none'}.{selectedCustomer && activeCustomerId !== selectedCustomer.id ? ' The explicit manual target differs.' : ''}</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Destination <span className="text-rose-400">*</span></label>
                <select value={target} onChange={(e) => setTarget(e.target.value as TargetProduct)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
                  <option>Microsoft Sentinel</option>
                  <option>Microsoft Defender for Endpoint</option>
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">Sentinel targets the selected workspace. MDE targets the selected Entra/Defender tenant.</p>
              </div>
            </div>

            <div className={`mt-4 rounded-xl border p-4 ${selectedCustomer ? 'border-cyan-400/25 bg-cyan-500/10' : 'border-amber-400/25 bg-amber-500/10'}`}>
              {selectedCustomer ? (
                <div className="grid gap-3 text-xs sm:grid-cols-2 xl:grid-cols-3">
                  <div><div className="text-muted-foreground">Target customer</div><div className="mt-1 font-black text-foreground">{selectedCustomer.name}</div></div>
                  <div><div className="text-muted-foreground">Tenant</div><div className="mt-1 break-all font-mono text-foreground">{selectedCustomer.tenantId || 'Not configured'}</div></div>
                  <div><div className="text-muted-foreground">Workspace</div><div className="mt-1 font-black text-foreground">{selectedCustomer.workspaceName || 'Not configured'}</div></div>
                  <div><div className="text-muted-foreground">Subscription</div><div className="mt-1 break-all font-mono text-foreground">{selectedCustomer.subscription || '-'}</div></div>
                  <div><div className="text-muted-foreground">Resource group</div><div className="mt-1 font-mono text-foreground">{selectedCustomer.resourceGroup || '-'}</div></div>
                  <div><div className="text-muted-foreground">Configured access</div><div className="mt-1 font-black text-foreground">{selectedCustomer.lighthouseMode ? 'Azure Lighthouse' : 'Customer SPN'}</div></div>
                  <div><div className="text-muted-foreground">Destination</div><div className="mt-1 font-black text-cyan-200">{target}</div></div>
                  <div className="sm:col-span-2"><div className="text-muted-foreground">Effective write authentication</div><div className="mt-1 font-black text-foreground">{target === 'Microsoft Sentinel' ? (selectedCustomer.lighthouseMode ? 'Azure Lighthouse / ARM resource access' : 'Customer SPN') : (selectedCustomer.lighthouseMode ? 'MSSP multitenant app → selected customer tenant' : 'Customer SPN → selected customer tenant')}</div></div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-200"><AlertTriangle size={16} /> Select a customer before validation or submission.</div>
              )}
            </div>

            {target === 'Microsoft Defender for Endpoint' && selectedCustomer?.lighthouseMode && (
              <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-500/10 p-3 text-xs leading-5 text-amber-100">
                <b>Important:</b> Azure Lighthouse does not authorize Defender tenant data-plane APIs. KloudSecure will request the MDE token from the selected customer tenant using the MSSP app identity and will block the write unless the token <code>tid</code> matches that customer tenant. The MSSP app therefore needs customer-tenant representation/admin consent for <code>Ti.ReadWrite.All</code>.
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h4 className="font-black text-foreground">2. Indicator details</h4>
            <p className="mt-1 text-xs text-muted-foreground">Build a structured observable where possible. Free-text STIX is Sentinel-only.</p>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Pattern mode</label>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-foreground">
                <label className="inline-flex items-center gap-2"><input type="radio" checked={mode === 'builder'} onChange={() => setMode('builder')} /> Pattern builder</label>
                <label className="inline-flex items-center gap-2"><input type="radio" checked={mode === 'freeText'} onChange={() => setMode('freeText')} disabled={target === 'Microsoft Defender for Endpoint'} /> Free-text STIX pattern</label>
              </div>
            </div>

            {mode === 'builder' ? (
              <div className="mt-4 rounded-xl border border-border bg-background/50 p-4">
                <div className="grid gap-3 md:grid-cols-[240px_1fr]">
                  <select value={observableType} onChange={(e) => setObservableType(e.target.value as ObservableType)} className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
                    {observableOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}{!opt.mde ? ' (Sentinel only)' : ''}</option>)}
                  </select>
                  <input value={observableValue} onChange={(e) => setObservableValue(e.target.value)} placeholder={selectedObservable?.placeholder} className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground" />
                </div>
                <div className="mt-3 rounded-xl border border-border bg-card p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Generated STIX pattern</div>
                  <code className="mt-1 block break-all text-xs text-cyan-200">{generatedPattern || '[observable:type = value]'}</code>
                </div>
                {target === 'Microsoft Defender for Endpoint' && !mdeSupported && (
                  <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-500/10 p-3 text-xs text-amber-100">This observable type is not supported by MDE custom indicators. Use IP, domain, URL, MD5, SHA1 or SHA256.</div>
                )}
              </div>
            ) : (
              <textarea value={freePattern} onChange={(e) => setFreePattern(e.target.value)} placeholder="[domain-name:value = 'evil.example.com']" className="mt-4 h-28 w-full rounded-xl border border-border bg-background p-3 font-mono text-xs text-foreground" />
            )}

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div><label className="text-sm font-medium text-foreground">Name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder={effectiveName || 'Auto-generated from observable'} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div><label className="text-sm font-medium text-foreground">Source <span className="text-rose-400">*</span></label><input value={source} onChange={(e) => setSource(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div><label className="text-sm font-medium text-foreground">Indicator types</label><input value={indicatorTypes} onChange={(e) => setIndicatorTypes(e.target.value)} placeholder="malicious-activity, anonymization" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div><label className="text-sm font-medium text-foreground">Kill chains</label><input value={killChain} onChange={(e) => setKillChain(e.target.value)} placeholder="reconnaissance, command-and-control" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div><label className="text-sm font-medium text-foreground">Valid from</label><input type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div><label className="text-sm font-medium text-foreground">Expiration</label><input type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div><label className="text-sm font-medium text-foreground">TLP</label><select value={tlp} onChange={(e) => setTlp(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"><option value="white">White</option><option value="green">Green</option><option value="amber">Amber</option><option value="red">Red</option></select></div>
              <div><label className="text-sm font-medium text-foreground">Tags</label><input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="customer-submission, phishing" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
              <div className="md:col-span-2"><label className="text-sm font-medium text-foreground">Description</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 h-24 w-full rounded-xl border border-border bg-background p-3 text-sm text-foreground" /></div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-5 rounded-xl border border-border bg-background/50 p-4">
              <label className="inline-flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={revoked} onChange={(e) => setRevoked(e.target.checked)} /> Revoked</label>
              <label className="inline-flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={confidenceNull} onChange={(e) => setConfidenceNull(e.target.checked)} /> Confidence is null</label>
              <label className="inline-flex items-center gap-2 text-sm text-foreground">Confidence <input type="number" min={0} max={100} value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} disabled={confidenceNull} className="w-24 rounded-lg border border-border bg-background px-2 py-1 text-sm disabled:opacity-50" /></label>
            </div>
          </section>

          {target === 'Microsoft Defender for Endpoint' && (
            <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h4 className="font-black text-foreground">3. MDE destination options</h4>
              <p className="mt-1 text-xs text-muted-foreground">These fields are sent to the selected customer's Defender custom indicator API after tenant validation succeeds.</p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div><label className="text-sm font-medium text-foreground">Action</label><select value={mdeAction} onChange={(e) => setMdeAction(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"><option>Block</option><option>BlockAndRemediate</option><option>Audit</option><option>Warn</option><option>Allowed</option><option>Alert</option><option>AlertAndBlock</option></select></div>
                <div><label className="text-sm font-medium text-foreground">Alert severity</label><select value={mdeSeverity} onChange={(e) => setMdeSeverity(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"><option>Informational</option><option>Low</option><option>Medium</option><option>High</option></select></div>
                <label className="mt-6 inline-flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={mdeGenerateAlert} onChange={(e) => setMdeGenerateAlert(e.target.checked)} /> Generate alert</label>
                <div><label className="text-sm font-medium text-foreground">Application label</label><input value={mdeApplication} onChange={(e) => setMdeApplication(e.target.value)} placeholder="Optional notification label" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
                <div><label className="text-sm font-medium text-foreground">RBAC group names</label><input value={mdeRbacGroupNames} onChange={(e) => setMdeRbacGroupNames(e.target.value)} placeholder="Comma-separated, blank = all devices" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
                <div><label className="text-sm font-medium text-foreground">RBAC group IDs</label><input value={mdeRbacGroupIds} onChange={(e) => setMdeRbacGroupIds(e.target.value)} placeholder="Comma-separated, optional" className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground" /></div>
                <div className="md:col-span-2"><label className="text-sm font-medium text-foreground">Recommended actions</label><textarea value={mdeRecommendedActions} onChange={(e) => setMdeRecommendedActions(e.target.value)} className="mt-1 h-20 w-full rounded-xl border border-border bg-background p-3 text-sm text-foreground" /></div>
              </div>
            </section>
          )}

          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h4 className="font-black text-foreground">{target === 'Microsoft Sentinel' ? '3' : '4'}. Validate and submit</h4>
            <p className="mt-1 text-xs text-muted-foreground">Validation is mandatory before submission. Any field change after validation requires another validation pass.</p>

            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => void validate()} disabled={!canValidate || !customerReady || validating} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-black text-slate-950 disabled:cursor-not-allowed disabled:opacity-40">
                {validating ? <RefreshCw size={16} className="animate-spin" /> : <ShieldCheck size={16} />} {validating ? 'Validating…' : 'Validate'}
              </button>
              <button type="button" onClick={() => void saveDraft()} disabled={!canDraft || !customerReady || saving} className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-bold text-foreground hover:bg-muted disabled:opacity-40"><Save size={16} /> {saving ? 'Saving…' : 'Save draft'}</button>
              <button type="button" onClick={() => setConfirmOpen(true)} disabled={!submitReady || uploading} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-black text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"><UploadCloud size={16} /> Review & submit</button>
            </div>

            {!validationCurrent && validation?.valid && <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-500/10 p-3 text-xs text-amber-100">The form changed after the last successful validation. Validate again before submission.</div>}
            {draftId && <div className="mt-3 text-[11px] text-muted-foreground">Current local draft ID: <code>{draftId}</code></div>}
            {message && <div className="mt-3 rounded-xl border border-cyan-400/25 bg-cyan-500/10 p-3 text-xs text-cyan-100">{message}</div>}

            {confirmOpen && submitReady && selectedCustomer && (
              <div className="mt-4 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={20} className="mt-0.5 text-emerald-300" />
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-emerald-100">Confirm target before write</div>
                    <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-3">
                      <div><span className="text-emerald-100/60">Customer</span><div className="font-black text-foreground">{selectedCustomer.name}</div></div>
                      <div><span className="text-emerald-100/60">Tenant</span><div className="break-all font-mono text-foreground">{selectedCustomer.tenantId || '-'}</div></div>
                      <div><span className="text-emerald-100/60">Destination</span><div className="font-black text-foreground">{target}</div></div>
                      {target === 'Microsoft Sentinel' && <div><span className="text-emerald-100/60">Workspace</span><div className="font-black text-foreground">{selectedCustomer.workspaceName || '-'}</div></div>}
                      {target === 'Microsoft Defender for Endpoint' && <div><span className="text-emerald-100/60">Validated token tenant</span><div className="break-all font-mono text-foreground">{validation?.targetAuth?.tokenTenantId || 'Not reported'}</div></div>}
                      <div><span className="text-emerald-100/60">Indicator</span><div className="break-all font-mono text-foreground">{observableValue || generatedPattern || '-'}</div></div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" onClick={() => void (target === 'Microsoft Sentinel' ? uploadSentinel() : uploadMde())} disabled={uploading} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-black text-slate-950 disabled:opacity-40"><UploadCloud size={16} /> {uploading ? 'Submitting…' : `Submit to ${target === 'Microsoft Sentinel' ? 'Sentinel' : 'MDE'}`}</button>
                      <button type="button" onClick={() => setConfirmOpen(false)} disabled={uploading} className="rounded-xl border border-border px-4 py-2 text-sm font-bold text-foreground hover:bg-muted">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <h4 className="flex items-center gap-2 font-black text-foreground"><Shield size={16} className="text-cyan-300" /> Validation result</h4>
            {!validation && <p className="mt-3 text-xs text-muted-foreground">Validate to check indicator syntax, customer context and destination authentication before any write.</p>}
            {validation && (
              <div className="mt-3 space-y-3 text-xs">
                <div className={`flex items-center gap-2 font-black ${validation.valid ? 'text-emerald-300' : 'text-rose-300'}`}>{validation.valid ? <CheckCircle2 size={15} /> : <XCircle size={15} />} {validation.valid ? 'Ready to submit' : 'Validation failed'}</div>
                {validation.targetAuth && (
                  <div className={`rounded-xl border p-3 ${validation.targetAuth.ok ? 'border-emerald-400/25 bg-emerald-500/10' : 'border-rose-400/25 bg-rose-500/10'}`}>
                    <div className="font-black text-foreground">Destination authentication</div>
                    <div className="mt-2 space-y-1 text-muted-foreground">
                      <div>Mode: <b className="text-foreground">{validation.targetAuth.authMode || '-'}</b></div>
                      <div>Target tenant: <code className="break-all text-foreground">{validation.targetAuth.targetTenantId || '-'}</code></div>
                      {target === 'Microsoft Defender for Endpoint' && <div>Token tenant: <code className="break-all text-foreground">{validation.targetAuth.tokenTenantId || '-'}</code></div>}
                      {validation.targetAuth.tenantMatch != null && <div>Tenant match: <b className={validation.targetAuth.tenantMatch ? 'text-emerald-300' : 'text-rose-300'}>{validation.targetAuth.tenantMatch ? 'Yes' : 'No'}</b></div>}
                      {validation.targetAuth.message && <div className="pt-1 leading-5">{validation.targetAuth.message}</div>}
                    </div>
                  </div>
                )}
                {validation.errors.length > 0 && <div><div className="font-black text-rose-300">Errors</div><ul className="mt-1 list-disc space-y-1 pl-4 text-rose-200">{validation.errors.map((x) => <li key={x}>{x}</li>)}</ul></div>}
                {validation.warnings.length > 0 && <div><div className="font-black text-amber-300">Warnings</div><ul className="mt-1 list-disc space-y-1 pl-4 text-amber-200">{validation.warnings.map((x) => <li key={x}>{x}</li>)}</ul></div>}
                {validation.pattern && <div><div className="font-black text-muted-foreground">Pattern</div><code className="mt-1 block break-all rounded-xl border border-border bg-background p-2 text-cyan-200">{validation.pattern}</code></div>}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <h4 className="flex items-center gap-2 font-black text-foreground"><Info size={16} className="text-cyan-300" /> Destination notes</h4>
            <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-muted-foreground">
              <li>Sentinel uses the selected workspace STIX upload API and Azure resource access.</li>
              <li>MDE is tenant-scoped. Azure Lighthouse alone cannot redirect a Defender API token into the customer tenant.</li>
              <li>For Lighthouse customers, KloudSecure requests an MDE token from the selected customer tenant and verifies the token <code>tid</code> before writing.</li>
              <li>If the MSSP app is not consented in that customer tenant, validation fails safely instead of writing to Kloudynet.</li>
            </ul>
          </div>

          {lastResponse != null && (
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <h4 className="font-black text-foreground">Last API response</h4>
              <pre className="mt-3 max-h-80 overflow-auto rounded-xl border border-border bg-background p-3 text-[11px] text-muted-foreground">{responseText(lastResponse)}</pre>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
